import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { getAuthHeader } from '../utils/auth';

const QUICK_PROMPTS = [
  "Why is my CPL high and what should I do?",
  "Which campaigns should I pause or scale?",
  "How can I improve my ad quality score?",
  "What's the best audience strategy for lead gen?",
  "My conversions dropped — what are the likely causes?",
  "Suggest a campaign structure for my account",
];

function formatMessage(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={i} className="font-semibold text-white mb-1">{line.slice(2, -2)}</p>;
    }
    if (line.startsWith('- ') || line.startsWith('• ')) {
      return <li key={i} className="ml-3 text-[#a1a1aa] mb-0.5">{line.slice(2)}</li>;
    }
    if (line.match(/^\d+\./)) {
      return <li key={i} className="ml-3 text-[#a1a1aa] mb-0.5 list-decimal">{line.slice(line.indexOf('.')+1).trim()}</li>;
    }
    if (line.trim() === '') return <div key={i} className="h-1.5" />;
    return <p key={i} className="text-[#a1a1aa] mb-1 leading-relaxed">{line}</p>;
  });
}

export default function AIChatWindow({ accounts = [], addToast }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your AI marketing assistant. Ask me anything about your campaigns, audiences, bidding strategy, or performance analysis.",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState('general');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const contextAccounts = accounts.filter(a => {
    if (context === 'meta') return a.platform === 'meta';
    if (context === 'google') return a.platform === 'google';
    return false;
  });

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setSelectedAccountId('');
  }, [context]);

  const sendMessage = useCallback(async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date() }]);
    setLoading(true);

    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          message: msg,
          context: context !== 'general' ? context : null,
          account_id: selectedAccountId ? parseInt(selectedAccountId) : null
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'AI service unavailable');
      }

      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply,
        timestamp: new Date(),
        context_used: data.context_used
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I couldn't process that request: ${err.message}. Please try again.`,
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, context, selectedAccountId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared. How can I help you with your campaigns?",
      timestamp: new Date()
    }]);
  };

  const CONTEXT_LABELS = {
    general: 'General',
    meta: 'Meta Ads',
    google: 'Google Ads',
    crm: 'CRM / Leads'
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#6366f1] hover:bg-[#5558e8] flex items-center justify-center shadow-lg shadow-[#6366f1]/30 transition-colors"
        title="AI Assistant"
      >
        {open ? <X className="w-5 h-5 text-white" /> : <MessageCircle className="w-5 h-5 text-white" />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-96 max-w-[calc(100vw-24px)] h-[600px] max-h-[80vh] flex flex-col rounded-2xl border border-[#27272a] bg-[#09090B]/95 backdrop-blur-xl shadow-2xl shadow-black/60">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272a] flex-shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#6366f1]" />
              <span className="text-sm font-semibold text-white" style={{fontFamily:'Manrope,sans-serif'}}>AI Assistant</span>
              <span className="text-xs text-[#52525b] bg-[#18181b] px-2 py-0.5 rounded-full">Beta</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} className="p-1.5 hover:bg-[#18181b] rounded-lg text-[#71717a] hover:text-white transition-colors" title="Clear chat">
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-[#18181b] rounded-lg text-[#71717a] hover:text-white transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Context Bar */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#27272a] flex-shrink-0 overflow-x-auto">
            {Object.entries(CONTEXT_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setContext(key)}
                className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${
                  context === key
                    ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/40'
                    : 'text-[#71717a] hover:text-white hover:bg-[#18181b]'
                }`}
              >
                {label}
              </button>
            ))}
            {(context === 'meta' || context === 'google') && contextAccounts.length > 0 && (
              <select
                value={selectedAccountId}
                onChange={e => setSelectedAccountId(e.target.value)}
                className="text-xs bg-[#18181b] border border-[#27272a] text-[#a1a1aa] rounded-lg px-2 py-1 ml-1 max-w-[140px]"
              >
                <option value="">All accounts</option>
                {contextAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                  msg.role === 'user'
                    ? 'bg-[#6366f1]/20 border border-[#6366f1]/30 text-white'
                    : msg.isError
                    ? 'bg-red-500/10 border border-red-500/20'
                    : 'bg-[#18181b] border border-[#27272a]'
                }`}>
                  <div className="text-sm leading-relaxed">
                    {msg.role === 'assistant' ? (
                      <div>{formatMessage(msg.content)}</div>
                    ) : (
                      <p className="text-sm text-white">{msg.content}</p>
                    )}
                  </div>
                  <p className="text-[10px] text-[#52525b] mt-1">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {msg.context_used && <span className="ml-1 text-[#6366f1]">· account data used</span>}
                  </p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#18181b] border border-[#27272a] rounded-2xl px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 text-[#6366f1] animate-spin" />
                    <span className="text-xs text-[#71717a]">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts (shown when only 1 message) */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex-shrink-0">
              <p className="text-[10px] text-[#52525b] mb-2 uppercase tracking-wider">Quick prompts</p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_PROMPTS.slice(0, 4).map((p, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(p)}
                    className="text-[11px] text-[#71717a] hover:text-white bg-[#18181b] hover:bg-[#27272a] border border-[#27272a] rounded-lg px-2 py-1 transition-colors text-left"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="px-3 pb-3 flex-shrink-0">
            <div className="flex items-end gap-2 bg-[#18181b] border border-[#27272a] rounded-xl p-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about campaigns, audiences, budgets..."
                className="flex-1 bg-transparent text-sm text-white placeholder-[#52525b] resize-none outline-none min-h-[36px] max-h-24"
                rows={1}
                style={{fontFamily:'Inter,sans-serif'}}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="p-1.5 rounded-lg bg-[#6366f1] hover:bg-[#5558e8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <Send className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
            <p className="text-[9px] text-[#3f3f46] mt-1 text-center">Ctrl+Enter to send</p>
          </div>
        </div>
      )}
    </>
  );
}
